import { useRef, useCallback, useEffect } from 'react';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  $getRoot,
  $getSelection,
  $isRangeSelection,
  $createParagraphNode,
  $createTextNode,
  $isElementNode,
  $isTextNode,
  $isLineBreakNode,
  FORMAT_TEXT_COMMAND,
  FORMAT_ELEMENT_COMMAND,
  COMMAND_PRIORITY_LOW,
  type EditorState,
  type LexicalEditor,
} from 'lexical';
import { HeadingNode, QuoteNode, $createHeadingNode, $createQuoteNode } from '@lexical/rich-text';
import { ListNode, ListItemNode, INSERT_ORDERED_LIST_COMMAND, INSERT_UNORDERED_LIST_COMMAND, REMOVE_LIST_COMMAND, $createListNode, $createListItemNode } from '@lexical/list';
import { $generateHtmlFromNodes } from '@lexical/html';

const theme = {};

function onError(error: Error) {
  console.error('Lexical Editor Error:', error);
}

interface RichTextEditorProps {
  initialHtml?: string;
  onChange?: (html: string) => void;
  editorRef?: React.MutableRefObject<LexicalEditor | null>;
}

function ToolbarPlugin() {
  const [editor] = useLexicalComposerContext();

  const formatBold = () => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold');
  const formatItalic = () => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic');
  const formatUnderline = () => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'underline');

  const formatH1 = () => editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'h1');
  const formatH2 = () => editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'h2');
  const formatH3 = () => editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'h3');
  const formatParagraph = () => editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'paragraph');
  const formatQuote = () => editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'quote');

  const insertBulletList = () => editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined);
  const insertNumberedList = () => editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined);

  const toolbarButtons = [
    { label: 'B', title: 'Bold', action: formatBold, className: 'font-bold' },
    { label: 'I', title: 'Italic', action: formatItalic, className: 'italic' },
    { label: 'U', title: 'Underline', action: formatUnderline, className: 'underline' },
  ];

  const headingButtons = [
    { label: 'H1', title: 'Heading 1', action: formatH1 },
    { label: 'H2', title: 'Heading 2', action: formatH2 },
    { label: 'H3', title: 'Heading 3', action: formatH3 },
    { label: 'P', title: 'Paragraph', action: formatParagraph },
  ];

  return (
    <div className="flex flex-wrap items-center gap-1 p-2 border-b border-background-200 bg-background-100 rounded-t-lg">
      <div className="flex items-center gap-0.5 pr-2 border-r border-background-200 mr-1">
        {toolbarButtons.map((btn) => (
          <button
            key={btn.title}
            type="button"
            title={btn.title}
            onClick={(e) => { e.preventDefault(); btn.action(); }}
            className={`w-7 h-7 flex items-center justify-center text-xs rounded hover:bg-background-200 text-foreground-700 transition-colors cursor-pointer ${btn.className}`}
          >
            {btn.label}
          </button>
        ))}
      </div>
      <div className="flex items-center gap-0.5 pr-2 border-r border-background-200 mr-1">
        {headingButtons.map((btn) => (
          <button
            key={btn.title}
            type="button"
            title={btn.title}
            onClick={(e) => { e.preventDefault(); btn.action(); }}
            className="w-7 h-7 flex items-center justify-center text-xs font-semibold rounded hover:bg-background-200 text-foreground-700 transition-colors cursor-pointer"
          >
            {btn.label}
          </button>
        ))}
      </div>
      <div className="flex items-center gap-0.5 pr-2 border-r border-background-200 mr-1">
        <button
          type="button"
          title="Bullet List"
          onClick={(e) => { e.preventDefault(); insertBulletList(); }}
          className="w-7 h-7 flex items-center justify-center text-xs rounded hover:bg-background-200 text-foreground-700 transition-colors cursor-pointer"
        >
          <i className="ri-list-unordered text-sm"></i>
        </button>
        <button
          type="button"
          title="Numbered List"
          onClick={(e) => { e.preventDefault(); insertNumberedList(); }}
          className="w-7 h-7 flex items-center justify-center text-xs rounded hover:bg-background-200 text-foreground-700 transition-colors cursor-pointer"
        >
          <i className="ri-list-ordered text-sm"></i>
        </button>
      </div>
      <div className="flex items-center gap-0.5">
        <button
          type="button"
          title="Blockquote"
          onClick={(e) => { e.preventDefault(); formatQuote(); }}
          className="w-7 h-7 flex items-center justify-center text-xs rounded hover:bg-background-200 text-foreground-700 transition-colors cursor-pointer"
        >
          <i className="ri-double-quotes-l text-sm"></i>
        </button>
      </div>
    </div>
  );
}

function OnChangeHtmlPlugin({ onChange }: { onChange: (html: string) => void }) {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    const removeListener = editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        const html = $generateHtmlFromNodes(editor);
        onChange(html);
      });
    });

    return removeListener;
  }, [editor, onChange]);

  return null;
}

function SetHtmlPlugin({ html }: { html: string }) {
  const [editor] = useLexicalComposerContext();
  const hasSet = useRef(false);

  useEffect(() => {
    if (hasSet.current) return;
    if (!html) return;

    hasSet.current = true;

    editor.update(() => {
      const root = $getRoot();
      root.clear();

      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const body = doc.body;

      function importNode(node: Node) {
        if (node.nodeType === Node.TEXT_NODE) {
          return $createTextNode(node.textContent || '');
        }
        if (node.nodeType === Node.ELEMENT_NODE) {
          const el = node as HTMLElement;
          const tag = el.tagName.toLowerCase();

          if (tag === 'h1') {
            const heading = $createHeadingNode('h1');
            el.childNodes.forEach((child) => heading.append(importNode(child)));
            return heading;
          }
          if (tag === 'h2') {
            const heading = $createHeadingNode('h2');
            el.childNodes.forEach((child) => heading.append(importNode(child)));
            return heading;
          }
          if (tag === 'h3') {
            const heading = $createHeadingNode('h3');
            el.childNodes.forEach((child) => heading.append(importNode(child)));
            return heading;
          }
          if (tag === 'blockquote') {
            const quote = $createQuoteNode();
            el.childNodes.forEach((child) => quote.append(importNode(child)));
            return quote;
          }
          if (tag === 'ul' || tag === 'ol') {
            const listType = tag === 'ul' ? 'bullet' : 'number';
            const listNode = $createListNode(listType, 1);
            el.childNodes.forEach((li) => {
              if (li.nodeType === Node.ELEMENT_NODE && (li as HTMLElement).tagName.toLowerCase() === 'li') {
                const itemNode = $createListItemNode();
                li.childNodes.forEach((child) => {
                  const childNode = importNode(child);
                  if ($isElementNode(childNode)) {
                    itemNode.append(childNode);
                  } else {
                    const wrapper = $createParagraphNode();
                    wrapper.append(childNode);
                    itemNode.append(wrapper);
                  }
                });
                listNode.append(itemNode);
              }
            });
            return listNode;
          }
          if (tag === 'li') {
            const itemNode = $createListItemNode();
            el.childNodes.forEach((child) => {
              const childNode = importNode(child);
              if ($isElementNode(childNode)) {
                itemNode.append(childNode);
              } else {
                const wrapper = $createParagraphNode();
                wrapper.append(childNode);
                itemNode.append(wrapper);
              }
            });
            return itemNode;
          }
          if (tag === 'strong' || tag === 'b') {
            const text = $createTextNode(el.textContent || '');
            text.setFormat('bold');
            return text;
          }
          if (tag === 'em' || tag === 'i') {
            const text = $createTextNode(el.textContent || '');
            text.setFormat('italic');
            return text;
          }
          if (tag === 'u') {
            const text = $createTextNode(el.textContent || '');
            text.setFormat('underline');
            return text;
          }
          if (tag === 'p' || tag === 'div') {
            const para = $createParagraphNode();
            el.childNodes.forEach((child) => para.append(importNode(child)));
            return para;
          }
          if (tag === 'br') {
            return $createTextNode('\n');
          }
          const textNode = $createTextNode(el.textContent || '');
          return textNode;
        }
        return $createTextNode('');
      }

      Array.from(body.childNodes).forEach((child) => {
        const node = importNode(child);
        // Only ElementNodes and DecoratorNodes can be appended directly to root.
        // TextNodes, LineBreakNodes etc. must be wrapped in a ParagraphNode.
        if ($isElementNode(node)) {
          root.append(node);
        } else if (node) {
          const p = $createParagraphNode();
          p.append(node);
          root.append(p);
        }
      });
    });
  }, [editor, html]);

  return null;
}

function EditorRefPlugin({ editorRef }: { editorRef?: React.MutableRefObject<LexicalEditor | null> }) {
  const [editor] = useLexicalComposerContext();
  useEffect(() => {
    if (editorRef) editorRef.current = editor;
  }, [editor, editorRef]);
  return null;
}

function LexicalErrorBoundaryImpl({ children }: { children: React.ReactNode }) {
  return <>{children}</>;
}

export default function RichTextEditor({ initialHtml = '', onChange, editorRef }: RichTextEditorProps) {
  const initialConfig = {
    namespace: 'BlogEditor',
    theme,
    onError,
    nodes: [HeadingNode, QuoteNode, ListNode, ListItemNode],
  };

  return (
    <LexicalComposer initialConfig={initialConfig}>
      <div className="border border-background-200 rounded-lg overflow-hidden">
        <ToolbarPlugin />
        <div className="relative">
          <RichTextPlugin
            contentEditable={
              <ContentEditable className="min-h-[300px] p-4 text-sm text-foreground-800 leading-relaxed outline-none bg-white rounded-b-lg prose prose-sm max-w-none" />
            }
            placeholder={
              <div className="absolute top-4 left-4 text-sm text-foreground-400 pointer-events-none select-none">
                Start writing your blog post...
              </div>
            }
            ErrorBoundary={LexicalErrorBoundaryImpl}
          />
        </div>
      </div>
      <HistoryPlugin />
      <OnChangeHtmlPlugin onChange={onChange} />
      <SetHtmlPlugin html={initialHtml} />
      <EditorRefPlugin editorRef={editorRef} />
    </LexicalComposer>
  );
}